// components/DuplicateConfirmationModal.js
import React from 'react';
import moment from 'moment';

const DuplicateConfirmationModal = ({
  show,
  onClose,
  onConfirm,
  conflictInfo,
  duplicateType,
  isSubmitting = false,
  isEdit = false
}) => {
  if (!show) return null;

  const formatDate = (dateString) => {
    return moment(dateString).format('MMM DD, YYYY');
  };

  const getModalTitle = () => {
    if (duplicateType === 'exact_match') {
      return 'Duplicate Found';
    } else {
      return 'Date Conflict';
    }
  };

  const getConflictDetails = () => {
    if (!conflictInfo) return null;

    const getStatusColor = (status) => {
      switch (status) {
        case 'active': return 'text-green-600';
        case 'completed': return 'text-blue-600';
        case 'inactive': return 'text-gray-600';
        default: return 'text-gray-600';
      }
    };

    return (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-gray-900 text-sm">{conflictInfo.name}</h4>
            <p className="text-gray-600 text-sm">
              {formatDate(conflictInfo.startDate)} - {formatDate(conflictInfo.endDate)}
            </p>
          </div>
          <span className={`text-sm font-medium capitalize ${getStatusColor(conflictInfo.status)}`}>
            {conflictInfo.status}
          </span>
        </div>
      </div>
    );
  };

  const getWarningMessage = () => {
    if (duplicateType === 'exact_match') {
      return "An academic year with the same name and dates already exists. This may cause confusion in reports and student assignments.";
    } else {
      return "The dates overlap with an existing academic year. This could lead to scheduling conflicts and data inconsistencies.";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md border border-gray-200">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{getModalTitle()}</h3>
              <p className="text-gray-600 text-sm mt-1">{getWarningMessage()}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {conflictInfo && getConflictDetails()}
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-blue-800 text-sm">
              <strong>Recommendation:</strong> {duplicateType === 'exact_match' 
                ? "Use a different name or adjust the dates." 
                : "Choose dates that don't overlap with existing academic years."}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                'Create Anyway'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateConfirmationModal;